import React, {useState, useEffect, useMemo} from 'react';
import { useTranslation } from 'react-i18next';
import styles from './CreateQualityCheckPlanModal.module.scss';
import { IconX, IconChevronRight, IconChevronDown } from '@tabler/icons-react';
import {PhaseDto, DeliverObjectDto, ProjectDto, LabelDto, SprintDto, Methods} from '../../api/model';
import { useProjectPhases } from '../../hooks/useProjectPhases';
import { useProjectDeliverables } from '../../hooks/useProjectDeliverables';
import {useGetSprint} from '../../api/sprint-api/sprint-api';
import {getPhaseDisplayName} from '../../utils/phaseUtils';

interface CreateQualityCheckPlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (planData: QualityCheckPlanData) => void;
  project?: ProjectDto;
}

export interface QualityCheckPlanData {
  designation: string;
  selectedPhaseIds: number[];
  selectedDeliverableIds: number[];
}

interface PhaseTreeNode {
  phase: PhaseDto;
  deliverables: DeliverObjectDto[];
  isExpanded: boolean;
  isSelected: boolean;
  isIndeterminate: boolean;
  selectedDeliverableIds: Set<number>;
}

interface SprintTreeNode {
  sprint: SprintDto;
  deliverables: DeliverObjectDto[];
  isExpanded: boolean;
  isSelected: boolean;
  isIndeterminate: boolean;
  selectedDeliverableIds: Set<number>;
}

const CreateQualityCheckPlanModal: React.FC<CreateQualityCheckPlanModalProps> = ({
  isOpen,
  onClose,
  onSave,
  project
}) => {
  const { t } = useTranslation('checkPlan');
  const [designation, setDesignation] = useState('');
  const [phaseTree, setPhaseTree] = useState<Map<number, PhaseTreeNode>>(new Map());
  const [sprintTree, setSprintTree] = useState<Map<number, SprintTreeNode>>(new Map());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [selectedFilterLabelIds, setSelectedFilterLabelIds] = useState<number[]>();
  const [isSprintSectionOpen, setIsSprintSectionOpen] = useState(false);
  const [isDeliverableSectionOpen, setIsDeliverableSectionOpen] = useState(false);

  const { data: projectPhases = [], isLoading: isPhasesLoading } = useProjectPhases(project?.id);
  const { deliverables: allDeliverables, isLoading: isDeliverablesLoading } = useProjectDeliverables(project);
  const {data: allSprints = []} = useGetSprint();

  const hasSprintsProject = project?.method === Methods.AGILE || project?.method === Methods.HYBRID;

  // Initialize phase tree
  useEffect(() => {
    if (!isOpen || !projectPhases.length) return;

    const newPhaseTree = new Map<number, PhaseTreeNode>();

    projectPhases.forEach(phase => {
      if (!phase.id) return;

      const phaseDeliverables = allDeliverables.filter((d: DeliverObjectDto) => {
        const deliverableAny = d as any;
        const phaseId = deliverableAny.phase_id || deliverableAny.phaseId;
        return phaseId === phase.id;
      });

      newPhaseTree.set(phase.id, {
        phase,
        deliverables: phaseDeliverables,
        isExpanded: false,
        isSelected: false,
        isIndeterminate: false,
        selectedDeliverableIds: new Set(),
      });
    });

    setPhaseTree(newPhaseTree);
  }, [isOpen, projectPhases, allDeliverables]);

  // Initialize sprint tree (only for AGILE/HYBRID projects)
  useEffect(() => {
    if (!isOpen || !hasSprintsProject) return;

    const projectPhaseIds = new Set(
        projectPhases.map(p => p.id).filter((id): id is number => id !== undefined)
    );

    const newSprintTree = new Map<number, SprintTreeNode>();

    allSprints
        .filter(s => s.id !== undefined && s.phase !== undefined && projectPhaseIds.has(s.phase!))
        .forEach(sprint => {
          if (!sprint.id) return;
          const sprintDeliverables = allDeliverables.filter(
              (d: DeliverObjectDto) => (d as any).sprint === sprint.id
          );
          newSprintTree.set(sprint.id, {
            sprint,
            deliverables: sprintDeliverables,
            isExpanded: false,
            isSelected: false,
            isIndeterminate: false,
            selectedDeliverableIds: new Set(),
          });
        });

    setSprintTree(newSprintTree);
  }, [isOpen, allSprints, allDeliverables, projectPhases, hasSprintsProject]);

  // All unique labels across deliverables in this project
  const allLabels = useMemo((): LabelDto[] => {
    const seen = new Map<number, LabelDto>();
    allDeliverables.forEach(d => {
      ((d as any).labels as LabelDto[] | undefined)?.forEach(label => {
        if (label.id !== undefined) seen.set(label.id, label);
      });
    });
    return Array.from(seen.values());
  }, [allDeliverables]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setDesignation('');
      setPhaseTree(new Map());
      setSprintTree(new Map());
      setErrors({});
      setSelectedFilterLabelIds([]);
      setIsSprintSectionOpen(false);
      setIsDeliverableSectionOpen(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const activeLabelIds = selectedFilterLabelIds ?? [];

  const isLabelMatched = (deliverable: DeliverObjectDto): boolean => {
    if (activeLabelIds.length === 0) return false;
    const labels = (deliverable as any).labels as LabelDto[] | undefined;
    return !!labels?.some(l => l.id !== undefined && activeLabelIds.includes(l.id));
  };

  const toggleFilterLabel = (labelId: number) => {
    setSelectedFilterLabelIds(prev =>
        (prev ?? []).includes(labelId)
            ? (prev ?? []).filter(id => id !== labelId)
            : [...(prev ?? []), labelId]
    );
  };

  // Label filter for the deliverable section
  const getFilteredDeliverables = (deliverables: DeliverObjectDto[]): DeliverObjectDto[] => {
    if (activeLabelIds.length === 0) return deliverables;
    return deliverables.filter(d => isLabelMatched(d));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!designation.trim()) {
      newErrors.designation = t('createModal.errors.designationRequired');
    } else if (designation.length > 50) {
      newErrors.designation = t('createModal.errors.designationMaxLength');
    }

    const hasLabelMatch = activeLabelIds.length > 0 && allDeliverables.some(d => isLabelMatched(d));
    const hasPhaseSelection = Array.from(phaseTree.values()).some(
        node => node.isSelected || node.selectedDeliverableIds.size > 0
    );
    const hasSprintSelection = Array.from(sprintTree.values()).some(
      node => node.isSelected || node.selectedDeliverableIds.size > 0
    );

    if (!hasPhaseSelection && !hasSprintSelection && !hasLabelMatch) {
      newErrors.selection = t('createModal.errors.selectionRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    const selectedPhaseIds: number[] = [];
    const selectedDeliverableIdsSet = new Set<number>();

    // Collect from phase tree
    for (const [phaseId, node] of phaseTree.entries()) {
      if (node.isSelected) {
        selectedPhaseIds.push(phaseId);
        node.deliverables.forEach(d => {
          if (d.id) selectedDeliverableIdsSet.add(d.id);
        });
      } else {
        node.selectedDeliverableIds.forEach(id => selectedDeliverableIdsSet.add(id));
        // Auto-selected by label filter
        node.deliverables.forEach(d => {
          if (d.id && isLabelMatched(d)) selectedDeliverableIdsSet.add(d.id);
        });
      }
    }

    // Collect from sprint tree
    for (const [, node] of sprintTree.entries()) {
      if (node.isSelected) {
        node.deliverables.forEach(d => {
          if (d.id) selectedDeliverableIdsSet.add(d.id);
        });
      } else {
        node.selectedDeliverableIds.forEach(id => selectedDeliverableIdsSet.add(id));
      }
    }

    onSave({
      designation,
      selectedPhaseIds,
      selectedDeliverableIds: Array.from(selectedDeliverableIdsSet),
    });
    onClose();
  };

  // Phase tree handlers
  const togglePhaseExpansion = (phaseId: number) => {
    setPhaseTree(prev => {
      const newTree = new Map(prev);
      const node = newTree.get(phaseId);
      if (node) newTree.set(phaseId, {...node, isExpanded: !node.isExpanded});
      return newTree;
    });
  };

  const handlePhaseCheckboxChange = (phaseId: number, checked: boolean) => {
    setPhaseTree(prev => {
      const newTree = new Map(prev);
      const node = newTree.get(phaseId);
      if (node) {
        node.isSelected = checked;
        node.isIndeterminate = false;
        if (checked) {
          node.selectedDeliverableIds = new Set(
            node.deliverables.map(d => d.id).filter((id): id is number => id !== undefined)
          );
        } else {
          node.selectedDeliverableIds.clear();
        }
        newTree.set(phaseId, { ...node });
      }
      return newTree;
    });
  };

  const handleDeliverableCheckboxChange = (phaseId: number, deliverableId: number, checked: boolean) => {
    setPhaseTree(prev => {
      const newTree = new Map(prev);
      const node = newTree.get(phaseId);
      if (node) {
        if (checked) {
          node.selectedDeliverableIds.add(deliverableId);
        } else {
          node.selectedDeliverableIds.delete(deliverableId);
        }
        const total = node.deliverables.filter(d => d.id !== undefined).length;
        const selected = node.selectedDeliverableIds.size;
        if (selected === 0) {
          node.isSelected = false;
          node.isIndeterminate = false;
        } else if (selected === total) {
          node.isSelected = true;
          node.isIndeterminate = false;
        } else {
          node.isSelected = false;
          node.isIndeterminate = true;
        }
        newTree.set(phaseId, { ...node });
      }
      return newTree;
    });
  };

  // Sprint tree handlers
  const toggleSprintExpansion = (sprintId: number) => {
    setSprintTree(prev => {
      const newTree = new Map(prev);
      const node = newTree.get(sprintId);
      if (node) newTree.set(sprintId, {...node, isExpanded: !node.isExpanded});
      return newTree;
    });
  };

  const handleSprintCheckboxChange = (sprintId: number, checked: boolean) => {
    setSprintTree(prev => {
      const newTree = new Map(prev);
      const node = newTree.get(sprintId);
      if (node) {
        node.isSelected = checked;
        node.isIndeterminate = false;
        if (checked) {
          node.selectedDeliverableIds = new Set(
              node.deliverables.map(d => d.id).filter((id): id is number => id !== undefined)
          );
        } else {
          node.selectedDeliverableIds.clear();
        }
        newTree.set(sprintId, {...node});
      }
      return newTree;
    });
  };

  const handleDeliverableInSprintCheckboxChange = (sprintId: number, deliverableId: number, checked: boolean) => {
    setSprintTree(prev => {
      const newTree = new Map(prev);
      const node = newTree.get(sprintId);
      if (node) {
        if (checked) {
          node.selectedDeliverableIds.add(deliverableId);
        } else {
          node.selectedDeliverableIds.delete(deliverableId);
        }
        const total = node.deliverables.filter(d => d.id !== undefined).length;
        const selected = node.selectedDeliverableIds.size;
        if (selected === 0) {
          node.isSelected = false;
          node.isIndeterminate = false;
        } else if (selected === total) {
          node.isSelected = true;
          node.isIndeterminate = false;
        } else {
          node.isSelected = false;
          node.isIndeterminate = true;
        }
        newTree.set(sprintId, {...node});
      }
      return newTree;
    });
  };

  const handleSelectAllSprints = (checked: boolean) => {
    setSprintTree(prev => {
      const newTree = new Map(prev);
      newTree.forEach((node, id) => {
        node.isSelected = checked;
        node.isIndeterminate = false;
        if (checked) {
          node.selectedDeliverableIds = new Set(
              node.deliverables.map(d => d.id).filter((id): id is number => id !== undefined)
          );
        } else {
          node.selectedDeliverableIds.clear();
        }
        newTree.set(id, {...node});
      });
      return newTree;
    });
  };

  const handleSelectAllPhases = (checked: boolean) => {
    setPhaseTree(prev => {
      const newTree = new Map(prev);
      newTree.forEach((node, id) => {
        node.isSelected = checked;
        node.isIndeterminate = false;
        if (checked) {
          node.selectedDeliverableIds = new Set(
              node.deliverables.map(d => d.id).filter((id): id is number => id !== undefined)
          );
        } else {
          node.selectedDeliverableIds.clear();
        }
        newTree.set(id, {...node});
      });
      return newTree;
    });
  };

  const sprintNodes = Array.from(sprintTree.values());
  const allSprintsSelected = sprintNodes.length > 0 && sprintNodes.every(n => n.isSelected);
  const someSprintsSelected = sprintNodes.some(n => n.isSelected || n.isIndeterminate);

  const phaseNodes = Array.from(phaseTree.values());
  const allPhasesSelected = phaseNodes.length > 0 && phaseNodes.every(n => n.isSelected);
  const somePhasesSelected = phaseNodes.some(n => n.isSelected || n.isIndeterminate);

  const isLoading = isPhasesLoading || isDeliverablesLoading;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3 className={styles.modalTitle}>{t('createModal.title')}</h3>
          <button className={styles.closeButton} onClick={onClose}>
            <IconX />
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.modalBody}>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>
              {t('createModal.designation')} <span className={styles.required}>*</span>
            </label>
            <input
              type="text"
              name="designation"
              className={styles.formInput}
              value={designation}
              onChange={(e) => setDesignation(e.target.value)}
              maxLength={50}
              placeholder={t('createModal.designationPlaceholder')}
            />
            {errors.designation && (
              <div className={styles.errorMessage}>{errors.designation}</div>
            )}
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>
              {t('createModal.selectPhasesAndDeliverables')} <span className={styles.required}>*</span>
            </label>

            {allLabels.length > 0 && (
                <div className={styles.labelFilterRow}>
                  <span className={styles.labelFilterTitle}>{t('createModal.filterByLabel')}</span>
                  <div className={styles.labelChips}>
                    {allLabels.map(label => {
                      const active = (selectedFilterLabelIds ?? []).includes(label.id!);
                      return (
                          <button
                              key={label.id}
                              type="button"
                              className={`${styles.labelChip} ${active ? styles.labelChipActive : ''}`}
                              style={
                                active && label.color
                                    ? {backgroundColor: label.color, borderColor: label.color, color: '#fff'}
                                    : label.color
                                        ? {borderColor: label.color, color: label.color}
                                        : undefined
                              }
                              onClick={() => toggleFilterLabel(label.id!)}
                          >
                            {label.color && !active && (
                                <span className={styles.labelDot} style={{backgroundColor: label.color}}/>
                            )}
                            {label.name}
                          </button>
                      );
                    })}
                    {(selectedFilterLabelIds ?? []).length > 0 && (
                        <button
                            type="button"
                            className={styles.clearLabelFilter}
                            onClick={() => setSelectedFilterLabelIds([])}
                        >
                          <IconX size={12}/> Clear
                        </button>
                    )}
                  </div>
                </div>
            )}

            <div className={styles.treeContainer}>
              {isLoading ? (
                <div className={styles.loadingMessage}>Loading phases and deliverables...</div>
              ) : (
                  <>
                    {/* Sprint section — only for AGILE / HYBRID projects */}
                    {hasSprintsProject && (
                        <>
                          <div
                              className={styles.sectionHeader}
                              onClick={() => setIsSprintSectionOpen(o => !o)}
                          >
                        <span className={styles.sectionHeaderTitle}>
                          {isSprintSectionOpen ? <IconChevronDown size={14}/> : <IconChevronRight size={14}/>}
                          By Sprint
                          {someSprintsSelected && !allSprintsSelected && (
                              <span
                                  className={styles.sectionSelectedBadge}>{sprintNodes.filter(n => n.isSelected || n.selectedDeliverableIds.size > 0).length} selected</span>
                          )}
                          {allSprintsSelected && (
                              <span className={styles.sectionSelectedBadge}>{t('createModal.allSelected')}</span>
                          )}
                        </span>
                            {isSprintSectionOpen && sprintTree.size > 0 && (
                                <label className={styles.selectAllLabel} onClick={e => e.stopPropagation()}>
                                  <input
                                      type="checkbox"
                                      checked={allSprintsSelected}
                                      ref={(input) => {
                                        if (input) input.indeterminate = !allSprintsSelected && someSprintsSelected;
                                      }}
                                      onChange={(e) => handleSelectAllSprints(e.target.checked)}
                                  />
                                  Select all
                                </label>
                            )}
                          </div>
                          {isSprintSectionOpen && (sprintTree.size === 0 ? (
                              <div className={styles.emptyMessage}>{t('createModal.noSprints')}</div>
                          ) : (
                              Array.from(sprintTree.entries()).map(([sprintId, node]) => (
                                  <div key={sprintId} className={styles.treeNode}>
                                    <div className={styles.treeNodeHeader}>
                                      <button
                                          type="button"
                                          className={styles.expandButton}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            toggleSprintExpansion(sprintId);
                                          }}
                                      >
                                        {node.isExpanded ? <IconChevronDown size={16}/> : <IconChevronRight size={16}/>}
                                      </button>
                                      <label className={styles.checkboxLabel}>
                                        <input
                                            type="checkbox"
                                            checked={node.isSelected}
                                            ref={(input) => {
                                              if (input) input.indeterminate = node.isIndeterminate;
                                            }}
                                            onChange={(e) => handleSprintCheckboxChange(sprintId, e.target.checked)}
                                        />
                                        <span className={styles.phaseLabel}>
                                  <strong>{node.sprint.name || `Sprint ${sprintId}`}</strong>
                                  <span className={styles.deliverableCount}>
                                    {` • ${node.deliverables.length} deliverable${node.deliverables.length !== 1 ? 's' : ''}`}
                                  </span>
                                </span>
                                      </label>
                                    </div>

                                    {node.isExpanded && node.deliverables.length > 0 && (
                                        <div className={styles.treeNodeChildren}>
                                          {node.deliverables.map((deliverable) => {
                                            if (!deliverable.id) return null;
                                            const checked = node.selectedDeliverableIds.has(deliverable.id);
                                            return (
                                                <label key={deliverable.id} className={styles.deliverableLabel}>
                                                  <input
                                                      type="checkbox"
                                                      checked={checked}
                                                      onChange={(e) =>
                                                          handleDeliverableInSprintCheckboxChange(sprintId, deliverable.id!, e.target.checked)
                                                      }
                                                  />
                                                  <span>{deliverable.name || t('createModal.unnamedDeliverable')}</span>
                                                  {deliverable.criticality && (
                                                      <span
                                                          className={styles.criticalityBadge}>{deliverable.criticality}</span>
                                                  )}
                                                </label>
                                            );
                                          })}
                                        </div>
                                    )}

                                    {node.isExpanded && node.deliverables.length === 0 && (
                                        <div className={styles.treeNodeChildren}>
                                          <div className={styles.emptyMessage}>No deliverables assigned to this sprint
                                          </div>
                                        </div>
                                    )}
                                  </div>
                              ))
                          ))}
                          <div className={styles.sectionDivider}/>
                        </>
                    )}

                    {/* Deliverable section */}
                    {hasSprintsProject && (
                        <div
                            className={styles.sectionHeader}
                            onClick={() => setIsDeliverableSectionOpen(o => !o)}
                        >
                      <span className={styles.sectionHeaderTitle}>
                        {isDeliverableSectionOpen ? <IconChevronDown size={14}/> : <IconChevronRight size={14}/>}
                        By Deliverable
                        {somePhasesSelected && !allPhasesSelected && (
                            <span
                                className={styles.sectionSelectedBadge}>{phaseNodes.filter(n => n.isSelected || n.selectedDeliverableIds.size > 0).length} selected</span>
                        )}
                        {allPhasesSelected && (
                            <span className={styles.sectionSelectedBadge}>{t('createModal.allSelected')}</span>
                        )}
                      </span>
                          {isDeliverableSectionOpen && phaseTree.size > 0 && (
                              <label className={styles.selectAllLabel} onClick={e => e.stopPropagation()}>
                                <input
                                    type="checkbox"
                                    checked={allPhasesSelected}
                                    ref={(input) => {
                                      if (input) input.indeterminate = !allPhasesSelected && somePhasesSelected;
                                    }}
                                    onChange={(e) => handleSelectAllPhases(e.target.checked)}
                                />
                                Select all
                              </label>
                          )}
                        </div>
                    )}
                    {(!hasSprintsProject || isDeliverableSectionOpen) && (phaseTree.size === 0 ? (
                        <div className={styles.emptyMessage}>{t('createModal.noPhases')}</div>
                    ) : (
                        Array.from(phaseTree.entries()).map(([phaseId, node]) => (
                            <div key={phaseId} className={styles.treeNode}>
                              <div className={styles.treeNodeHeader}>
                                <button
                                    type="button"
                                    className={styles.expandButton}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      togglePhaseExpansion(phaseId);
                                    }}
                                >
                                  {node.isExpanded ? <IconChevronDown size={16}/> : <IconChevronRight size={16}/>}
                                </button>
                                <label className={styles.checkboxLabel}>
                                  <input
                                      type="checkbox"
                                      checked={node.isSelected}
                                      ref={(input) => {
                                        if (input) input.indeterminate = node.isIndeterminate;
                                      }}
                                      onChange={(e) => handlePhaseCheckboxChange(phaseId, e.target.checked)}
                                  />
                                  <span className={styles.phaseLabel}>
                              <strong>{getPhaseDisplayName(node.phase)}</strong>
                                    {node.phase.subTypeAgile && ` (${node.phase.subTypeAgile})`}
                                    <span className={styles.deliverableCount}>
                                {` • ${node.deliverables.length} deliverable${node.deliverables.length !== 1 ? 's' : ''}`}
                              </span>
                            </span>
                                </label>
                              </div>

                              {node.isExpanded && node.deliverables.length > 0 && (
                                  <div className={styles.treeNodeChildren}>
                                    {getFilteredDeliverables(node.deliverables).map((deliverable) => {
                                      if (!deliverable.id) return null;
                                      const autoSelected = isLabelMatched(deliverable);
                                      const checked = autoSelected || node.selectedDeliverableIds.has(deliverable.id);
                                      return (
                                          <label
                                              key={deliverable.id}
                                              className={`${styles.deliverableLabel} ${autoSelected ? styles.deliverableLabelAuto : ''}`}
                                          >
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                disabled={autoSelected}
                                                onChange={(e) =>
                                                    !autoSelected && handleDeliverableCheckboxChange(phaseId, deliverable.id!, e.target.checked)
                                                }
                                            />
                                            <span>{deliverable.name || t('createModal.unnamedDeliverable')}</span>
                                            {deliverable.criticality && (
                                                <span
                                                    className={styles.criticalityBadge}>{deliverable.criticality}</span>
                                            )}
                                          </label>
                                      );
                                    })}
                                  </div>
                              )}

                              {node.isExpanded && getFilteredDeliverables(node.deliverables).length === 0 && (
                                  <div className={styles.treeNodeChildren}>
                                    <div className={styles.emptyMessage}>
                                      {node.deliverables.length === 0
                                          ? t('createModal.noDeliverablesInPhase')
                                          : t('createModal.noDeliverablesMatchFilters')}
                                    </div>
                                  </div>
                              )}
                            </div>
                        ))
                    ))}
                  </>
              )}
            </div>

            {errors.selection && <div className={styles.errorMessage}>{errors.selection}</div>}
            {errors.submit && <div className={styles.errorMessage}>{errors.submit}</div>}
          </div>

          <div className={styles.formActions}>
            <button type="button" className={styles.cancelButton} onClick={onClose}>
              {t('createModal.cancel')}
            </button>
            <button type="submit" className={styles.submitButton}>
              {t('createModal.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateQualityCheckPlanModal;
